import { useEffect, useRef, useState } from "react";

// Universal 3-2-1 GO overlay for games that expose a start button. Games
// without a start button (auto-playing quizzes, LuckyScratch's bomb timer,
// level pickers) render zero overlay so the user never sees a second
// countdown on top of the game's own timer.
const GameCountdown = ({ hostRef, gameId }) => {
  const [n, setN] = useState(null); // null = deciding; number = countdown; -1 = gone
  const clickedRef = useRef(false);
  const startBtnRef = useRef(null);

  // Reset whenever the mounted game changes.
  useEffect(() => {
    clickedRef.current = false;
    startBtnRef.current = null;
    setN(null);
  }, [gameId]);

  // Detect a start button inside the game host. We retry a few times because
  // the game is lazy-loaded and may not be in the DOM on the first tick.
  useEffect(() => {
    if (n !== null) return;
    let cancelled = false;
    let tries = 0;
    const scan = () => {
      if (cancelled) return;
      const host = hostRef?.current;
      const btn = host?.querySelector(
        '[data-testid$="-start-btn"]:not([disabled])'
      );
      if (btn) {
        startBtnRef.current = btn;
        setN(3);
        return;
      }
      tries += 1;
      if (tries >= 20) {
        // No start button after ~1s — this game auto-plays / needs a picker.
        // Skip the overlay entirely so we do not stack a fake countdown on top
        // of the game's own timer.
        setN(-1);
        return;
      }
      setTimeout(scan, 50);
    };
    scan();
    return () => {
      cancelled = true;
    };
  }, [gameId, n, hostRef]);

  // Drive the visible 3 → 2 → 1 → GO! sequence.
  useEffect(() => {
    if (n === null || n === -1) return;
    if (n <= 0) {
      if (!clickedRef.current) {
        clickedRef.current = true;
        // Re-query at click time so we always hit the LIVE start button in
        // the DOM (React can replace nodes between renders, which would
        // leave the ref pointing at a detached element). Retry once at
        // 120ms in case the DOM was mid-render on the first attempt.
        const clickStart = () => {
          const host = hostRef?.current;
          const live = host?.querySelector(
            '[data-testid$="-start-btn"]:not([disabled])'
          );
          if (live) {
            live.click();
            return true;
          }
          return false;
        };
        if (!clickStart()) {
          setTimeout(clickStart, 120);
        }
      }
      const hide = setTimeout(() => setN(-1), 350);
      return () => clearTimeout(hide);
    }
    const t = setTimeout(() => setN((v) => (v ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [n, hostRef]);

  if (n === null || n === -1) return null;

  const label = n > 0 ? String(n) : "GO!";
  return (
    <div
      className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
      aria-live="polite"
      data-testid="game-countdown"
      data-count={label}
      style={{
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        borderRadius: 18,
      }}
    >
      <div
        key={label}
        className="font-display bob"
        style={{
          fontSize: n > 0 ? 96 : 72,
          color: n > 0 ? "#FF5A5F" : "#54D62C",
          textShadow: "0 4px 0 #2b2b2b",
          animation: "pop-in 220ms ease-out",
        }}
      >
        {label}
      </div>
    </div>
  );
};

export default GameCountdown;
